import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  ICreateNotificationRemindRequest,
  IUpdateNotificationRemindRequest,
  NotificationRemindType,
} from '@/types/request';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  notificationStatus,
  NOTIFY_STATUS,
} from '@/utils';
import { ERROR, SUCCESS, SUCCESS_MESSAGE } from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, TableColumnsType } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { NotificationForm } from '../Drawers';
import { formSchemaFn } from '../Drawers/NotificationForm/formSchemaFn';
import { FORM_NAME } from '../Drawers/NotificationForm/constants';
import { NotificationRemindService } from '@/services';
import { IGetDetailNotificationRemindResponse } from '@/types/response';

interface IProps {
  title?: string;
}

interface IParams {
  limit: number;
  page: number;
  type?: string;
}

enum FilterTypeLabel {
  TEETH = '歯科検診受診日登録のお知らせ',
}

export const notificationRemindTypeOptions = [
  {
    value: NotificationRemindType.TEETH,
    label: FilterTypeLabel.TEETH,
  },
];

const messageTemplate = '前回の歯科検診の受診日を登録していない方は登録しよう！';

export const NotificationRemindList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailNotificationRemindResponse>();
  const [isNotPermissionEdit, setIsNotPermissionEdit] =
    useState<boolean>(false);
  const [isNotFoundDetail, setIsNotFoundDetail] = useState<boolean>(false);
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.NOTIFICATION_REMIND.GET_LIST, params],
    () => NotificationRemindService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId!, isNotPermissionEdit)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, watch, setValue, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const handleCreate = (values: ICreateNotificationRemindRequest) => {
    const payload = {
      title: messageTemplate,
      message: messageTemplate,
      status: values.status ? 1 : 0,
      pushNotificationDate: moment(values?.pushNotificationDate).utc(),
      type: NotificationRemindType.TEETH,
    };

    createRemindNotification(payload);
  };

  const {
    mutate: createRemindNotification,
    isLoading: isLoadingCreateNotificationRemind,
  } = useMutation(
    (body: ICreateNotificationRemindRequest) =>
      NotificationRemindService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
              SUCCESS.CREATE_NOTIFICATION_REMIND as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail, refetch: refetchDetail } = useQuery(
    [ENDPOINT.NOTIFICATION_REMIND.GET_DETAIL.replace(':id', String(currentId))],
    () => NotificationRemindService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (
        response: IResponse<IGetDetailNotificationRemindResponse>,
      ) => {
        const data = response?.data;
        setDetail(data);

        reset({
          formName: FORM_NAME.UPDATE_NOTIFICATION_REMIND,
          title: messageTemplate,
          message: messageTemplate,
          status: data?.status ? 1 : 0,
          type: data?.type,
          pushNotificationDate: moment(data?.pushNotificationDate),
          isDisabled: !!data?.pushNotificationAt,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
        setIsNotFoundDetail(true);
      },
    },
  );

  const handleUpdate = (values: IUpdateNotificationRemindRequest) => {
    const payload = {
      id: currentId!,
      body: {
        title: messageTemplate,
        message: messageTemplate,
        status: values.status ? 1 : 0,
        pushNotificationDate: moment(values?.pushNotificationDate).utc(),
        type: values.type,
      },
    };

    updateNotificationRemind(payload);
  };

  const handleDelete = (data: IGetDetailNotificationRemindResponse) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: (
        <DeleteContent
          name='配信日時'
          content={moment(data?.pushNotificationDate).format(
            'YYYY年MM月DD日 HH:mm',
          )}
        />
      ),
      maskClosable: false,
      onOk: () => deleteNotificationRemind(currentId!),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  const {
    mutate: updateNotificationRemind,
    isLoading: isLoadingUpdateNotificationRemind,
  } = useMutation(
    (payload: { body: IUpdateNotificationRemindRequest; id: number }) =>
      NotificationRemindService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
              SUCCESS.UPDATE_NOTIFICATION_REMIND as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.NOTIFICATION_REMIND_NOT_FOUND) {
          setOpenDrawer(false);
          refetch();
        }

        if (error.error === ERROR.NOTIFICATION_REMIND_RELOAD_DATA) {
          refetch();
        }

        if (error.error === ERROR.CANT_ACTION_NOTIFICATION_REMIND_PUBLISHED) {
          refetchDetail();
        }
      },
    },
  );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const {
    mutate: deleteNotificationRemind,
    isLoading: isLoadingDeleteNotificationRemind,
  } = useMutation((id: number) => NotificationRemindService.delete(id), {
    onSuccess: () => {
      notificationMessage({
        message:
          SUCCESS_MESSAGE[
            SUCCESS.DELETE_NOTIFICATION_REMIND as keyof typeof SUCCESS_MESSAGE
          ],
        type: NOTIFY_STATUS.SUCCESS,
      });
      refetch();
      handleCancel();
      setParams({ ...params, page: 1 });
    },
    onError: (error: IResponseError) => {
      handleErrorCommon(error, setError);

      if (error.error === ERROR.NOTIFICATION_REMIND_NOT_FOUND) {
        setOpenDrawer(false);
      }

      if (error.error === ERROR.NOTIFICATION_REMIND_RELOAD_DATA) {
        setOpenDrawer(false);
        refetch();
      }
    },
  });

  const columns: TableColumnsType = [
    {
      title: '配信日時',
      width: '400px',
      render: (value, record) => {
        return `${moment(record.pushNotificationDate).format('YYYY年MM月DD日 HH:mm')}`;
      },
    },
    {
      title: '通知内容',
      dataIndex: 'message',
    },
    {
      title: 'ステータス',
      width: '200px',
      render: (value, record) => {
        if (!record.status) {
          return notificationStatus[1].label;
        }

        return record.pushNotificationAt ? '配信中' : '配信予約済み';
      },
    },
  ];

  const isDisabled = watch('isDisabled');

  useEffect(() => {
    setIsNotPermissionEdit(isDisabled);

    if (isNotFoundDetail) {
      setOpenDrawer(false);
      refetch();
    }
  }, [isDisabled, isNotFoundDetail]);

  const handleSelectFilter = (value: string) => {
    setParams({ ...params, type: value, page: 1 });
  };

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <header className='text-right'>
          {/* <Select
            className='min-w-[170px] mr-3 text-left'
            defaultValue={null}
            onChange={handleSelectFilter}
            options={[
              { value: null, label: '-' },
              ...notificationRemindTypeOptions,
            ]}
          /> */}
          <Button
            type='primary'
            onClick={() => {
              setOpenDrawer(true),
                reset({ message: messageTemplate }),
                setValue('status', 0),
                setIsNotFoundDetail(false);
            }}
          >
            お知らせ新規作成
          </Button>
        </header>

        <div className='flex justify-between flex-col h-full'>
          <Table
            loading={isFetching}
            scroll={{ x: 800 }}
            bordered
            rowKey='id'
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
                setIsNotFoundDetail(false);
              },
            })}
            locale={{
              emptyText: 'お知らせはまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!currentId ? 'お知らせ新規登録' : 'お知らせせ詳細'}
        onOk={handleSubmit(currentId ? handleUpdate : handleCreate)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          currentId
            ? isLoadingUpdateNotificationRemind ||
              isLoadingDeleteNotificationRemind ||
              isFetchingDetail
            : isLoadingCreateNotificationRemind
        }
        isDeleted={!!currentId}
        disabled={isDisabled}
        allowDelete
      >
        <FormProvider {...methods}>
          <NotificationForm isLoading={isFetchingDetail} />
        </FormProvider>
      </Drawer>
    </div>
  );
};
